import { getLocalStorage } from '@libs/storage';

const STORAGE_KEY = 'affiliate';
const AFFILIATE_EXPIRATION_DAYS = 15;

interface StoredAffiliate {
  source: string;
  capturedAt: number;
}

/**
 * Сервис для отслеживания партнёрских реферальных ссылок.
 *
 * Захватывает utm_source из URL при условии utm_medium=affiliate,
 * сохраняет в localStorage и добавляет к исходящим ссылкам.
 */
export class AffiliateService {
  private constructor() {
    this.#loadFromStorage();
    this.#captureFromUrl();
  }

  #storage = getLocalStorage();
  #cached: StoredAffiliate | null = null;

  get source(): string | null {
    if (!this.#cached) return null;

    const expirationMs = AFFILIATE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - this.#cached.capturedAt > expirationMs) {
      this.clear();
      return null;
    }

    return this.#cached.source;
  }

  capture(source: string): void {
    this.#cached = {
      source,
      capturedAt: Date.now(),
    };

    this.#storage.setItem(STORAGE_KEY, JSON.stringify(this.#cached));
  }

  clear(): void {
    this.#cached = null;
    this.#storage.removeItem(STORAGE_KEY);
  }

  #loadFromStorage(): void {
    const data = this.#storage.getItem(STORAGE_KEY);
    if (!data) return;

    try {
      this.#cached = JSON.parse(data);
    } catch {
      this.#storage.removeItem(STORAGE_KEY);
    }
  }

  #captureFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const medium = params.get('utm_medium');

    if (source && medium === 'affiliate' && /^[a-zA-Z0-9_-]{2,50}$/.test(source)) {
      this.capture(source);
    }
  }

  appendToUrl(url: string): string {
    const source = this.source;
    if (!source) return url;

    const isRelative = url.startsWith('/') && !url.startsWith('//');
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('utm_source', source);
    parsed.searchParams.set('utm_medium', 'affiliate');

    return isRelative
      ? parsed.toString().substring(window.location.origin.length)
      : parsed.toString();
  }

  // Statics

  static #instance: AffiliateService;

  static get instance(): AffiliateService {
    if (!this.#instance) {
      this.#instance = new AffiliateService();
    }
    return this.#instance;
  }
}
